import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Phone, Mail, MapPin, Clock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BRAND, PROJECT_TYPES } from "@/lib/site";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  location: "",
  project_type: "",
  message: "",
};

export default function Contact() {
  const navigate = useNavigate();
  const [form, setForm] = React.useState(initial);
  const [submitting, setSubmitting] = React.useState(false);
  const [errors, setErrors] = React.useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please tell us your name";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.location.trim()) e.location = "Please tell us where the site is";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "A short description helps us respond faster (min. 10 characters)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please check the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await axios.post(`${API}/enquiries`, payload);
      // Fire Google Ads conversion event (no-op under Consent Mode v2 if ad_storage denied).
      // GA4 `generate_lead` event is fired from ThankYou.jsx on page mount so it correlates
      // with page_path=/thank-you (single source of truth for the Key Event in GA4).
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        try {
          window.gtag("event", "conversion", {
            send_to: "AW-612532606",
            value: 50.0,
            currency: "GBP",
          });
        } catch { /* ignore */ }
      }
      setForm(initial);
      navigate("/thank-you");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full bg-white border border-zinc-300 px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-[#C2410C] focus:ring-1 focus:ring-[#C2410C] transition-colors";

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact"
        description="Request a concrete repair survey or specification. Nationwide UK coverage. Get in touch with Concrete Insight UK Ltd for a diagnostic-first assessment of your reinforced concrete structure."
      />
      {/* Header */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-16 md:pt-32 md:pb-24">
          <div className="tech-label reveal">// Contact · Start an enquiry</div>
          <h1 className="hero-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl mt-6 max-w-5xl reveal reveal-delay-1">
            Tell us about<br />the structure.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-zinc-700 leading-relaxed reveal reveal-delay-2">
            The more we know about what you&apos;re seeing, the faster we can come
            back with a useful response. Photographs, location, and a short
            description are usually enough to get started.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left column: contact info */}
          <aside className="lg:col-span-4 space-y-8">
            <div>
              <div className="tech-label">// Direct</div>
              <div className="mt-4 space-y-4">
                <a href={`mailto:${BRAND.email}`} className="flex items-start gap-3 group" data-testid="contact-email-link">
                  <Mail size={16} className="mt-1 text-[#C2410C]" strokeWidth={2} />
                  <div>
                    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">Email</div>
                    <div className="text-zinc-900 group-hover:text-[#C2410C] transition-colors">{BRAND.email}</div>
                  </div>
                </a>
                <a href={`tel:${BRAND.phoneDial}`} className="flex items-start gap-3 group" data-testid="contact-phone-link">
                  <Phone size={16} className="mt-1 text-[#C2410C]" strokeWidth={2} />
                  <div>
                    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">Phone</div>
                    <div className="text-zinc-900 group-hover:text-[#C2410C] transition-colors">{BRAND.phone}</div>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <MapPin size={16} className="mt-1 text-[#C2410C]" strokeWidth={2} />
                  <div>
                    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">Office</div>
                    <div className="text-zinc-900" data-testid="contact-address">
                      {BRAND.address.line1}<br />
                      {BRAND.address.line2}<br />
                      {BRAND.address.postcode}
                    </div>
                    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mt-3">Coverage</div>
                    <div className="text-zinc-900">{BRAND.areaOfOperation}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock size={16} className="mt-1 text-[#C2410C]" strokeWidth={2} />
                  <div>
                    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">Hours</div>
                    <div className="text-zinc-900">{BRAND.hours}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-zinc-900 p-6 relative">
              <span className="corner-tick tl" />
              <span className="corner-tick tr" />
              <span className="corner-tick bl" />
              <span className="corner-tick br" />
              <div className="tech-label">// Quick brief</div>
              <p className="mt-3 text-sm text-zinc-700 leading-relaxed">
                A useful enquiry usually includes: what you&apos;re seeing (staining,
                spalling, cracking), where on the structure, and any recent
                context (leaks, repairs, age).
              </p>
            </div>

            <div>
              <div className="tech-label">// Response time</div>
              <div className="font-display text-4xl md:text-5xl font-black uppercase mt-3">
                &lt; 24h
              </div>
              <p className="text-sm text-zinc-600 mt-2">Weekday enquiries answered within one working day.</p>
            </div>
          </aside>

          {/* Right column: form */}
          <div className="lg:col-span-8">
            <form
              onSubmit={onSubmit}
              className="border border-zinc-900 p-6 md:p-10 bg-white relative"
              data-testid="contact-form"
            >
              <span className="corner-tick tl" />
              <span className="corner-tick tr" />
              <span className="corner-tick bl" />
              <span className="corner-tick br" />

              <div className="tech-label">// Enquiry form</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight mt-3 leading-tight">
                Send us the details
              </h2>

              <div className="rule-line my-8 bg-zinc-200" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Name" required error={errors.name}>
                  <input
                    type="text"
                    className={inputBase}
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="Your full name"
                    data-testid="contact-input-name"
                  />
                </Field>
                <Field label="Company / Organisation">
                  <input
                    type="text"
                    className={inputBase}
                    value={form.company}
                    onChange={(e) => set("company", e.target.value)}
                    placeholder="Optional"
                    data-testid="contact-input-company"
                  />
                </Field>
                <Field label="Email" required error={errors.email}>
                  <input
                    type="email"
                    className={inputBase}
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@example.com"
                    data-testid="contact-input-email"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    type="tel"
                    className={inputBase}
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="Optional"
                    data-testid="contact-input-phone"
                  />
                </Field>
                <Field label="Site location" required error={errors.location}>
                  <input
                    type="text"
                    className={inputBase}
                    value={form.location}
                    onChange={(e) => set("location", e.target.value)}
                    placeholder="e.g. Manchester city centre"
                    data-testid="contact-input-location"
                  />
                </Field>
                <Field label="Project type">
                  <Select
                    value={form.project_type}
                    onValueChange={(v) => set("project_type", v)}
                  >
                    <SelectTrigger
                      className="w-full rounded-none border-zinc-300 h-[46px] focus:ring-[#C2410C] focus:ring-1 data-[state=open]:border-[#C2410C]"
                      data-testid="contact-select-project-type"
                    >
                      <SelectValue placeholder="Select a type" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {PROJECT_TYPES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`project-type-option-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Describe what you're seeing" required error={errors.message}>
                  <textarea
                    rows={6}
                    className={inputBase + " resize-y"}
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    placeholder="Staining, spalling, cracks, age of structure, any recent context…"
                    data-testid="contact-input-message"
                  />
                </Field>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? "Sending…" : "Send enquiry"}{" "}
                  <ArrowUpRight className="arrow" size={14} strokeWidth={2.5} />
                </button>
                <div className="text-xs text-zinc-500 font-mono uppercase tracking-[0.16em]">
                  // We reply within one working day
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
        {label} {required && <span className="text-[#C2410C]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
      {error && (
        <span className="mt-1.5 block text-xs text-red-600" data-testid="field-error">
          {error}
        </span>
      )}
    </label>
  );
}
